
import LedLightingForm from './forms/LedLightingForm';
import RooftopSolarForm from './forms/RooftopSolarForm';
import HvacOptimizationForm from './forms/HvacOptimizationForm';
import TemperatureReductionForm from './forms/TemperatureReductionForm';
import GreenTariffPpaForm from './forms/GreenTariffPpaForm';
import FleetElectrificationForm from './forms/FleetElectrificationForm';
import { formatDecimalNumber } from '../reports/reportHelper';

export const SCENARIO_FORM_MAP = {
  led: LedLightingForm,
  solar: RooftopSolarForm,
  hvac: HvacOptimizationForm,
  temp: TemperatureReductionForm,
  ppa: GreenTariffPpaForm,
  fleet: FleetElectrificationForm,
};

export const ActivityTypeEnum = {
  // ===== Fuel Combustion =====
  FUEL_DIESEL: 'FUEL_DIESEL',
  FUEL_LNG: 'FUEL_LNG',
  FUEL_BIODIESEL: 'FUEL_BIODIESEL',
  FUEL_GASOLINE: 'FUEL_GASOLINE',
  FUEL_NATURAL_GAS_GENERAL: 'FUEL_NATURAL_GAS_GENERAL',
  FUEL_NATURAL_GAS_HVAC: 'FUEL_NATURAL_GAS_HVAC',
  FUEL_PROPANE: 'FUEL_PROPANE',
  FUEL_COAL: 'FUEL_COAL',
  FUEL_OTHER_SOLID: 'FUEL_OTHER_SOLID',
  FUEL_OTHER_LIQUID: 'FUEL_OTHER_LIQUID',
  FUEL_OTHER_GAS: 'FUEL_OTHER_GAS',

  // ===== Water Use =====
  WATER_WITHDRAWN: 'WATER_WITHDRAWN',
  WATER_DISCHARGED: 'WATER_DISCHARGED',
  WATER_RECYCLED: 'WATER_RECYCLED',

  // ===== Electricity Use =====
  ELECTRICITY_GENERAL: 'ELECTRICITY_GENERAL',
  ELECTRICITY_HVAC: 'ELECTRICITY_HVAC',
  ELECTRICITY_PRODUCTION: 'ELECTRICITY_PRODUCTION',
  ELECTRICITY_SOLAR_PV: 'ELECTRICITY_SOLAR_PV',
  ELECTRICITY_LIGHTING: 'ELECTRICITY_LIGHTING',
  ELECTRICITY_EV_CHARGING: 'ELECTRICITY_EV_CHARGING',

  // ===== HVAC =====
  HVAC_OTHER: 'HVAC_OTHER',
  HVAC_REFRIGERANT: 'HVAC_REFRIGERANT',
  HVAC_DISTRICT_HEATING: 'HVAC_DISTRICT_HEATING',
  HVAC_DISTRICT_COOLING: 'HVAC_DISTRICT_COOLING',

  // ===== Transportation =====
  TRANSPORT_PETROL_CAR: 'TRANSPORT_PETROL_CAR',
  TRANSPORT_DIESEL_CAR: 'TRANSPORT_DIESEL_CAR',
  TRANSPORT_ELECTRIC_CAR: 'TRANSPORT_ELECTRIC_CAR',
  TRANSPORT_HYBRID_CAR: 'TRANSPORT_HYBRID_CAR',
  TRANSPORT_PUBLIC: 'TRANSPORT_PUBLIC',
  TRANSPORT_WALKING: 'TRANSPORT_WALKING',
  TRANSPORT_FLIGHT: 'TRANSPORT_FLIGHT',
  TRANSPORT_MOTORCYCLE: 'TRANSPORT_MOTORCYCLE',
  TRANSPORT_BOAT: 'TRANSPORT_BOAT',

  // ===== Waste =====
  WASTE_HAZARDOUS: 'WASTE_HAZARDOUS',
  WASTE_NON_HAZARDOUS: 'WASTE_NON_HAZARDOUS',
  WASTE_RECYCLED: 'WASTE_RECYCLED',
  WASTE_COMBUSTED: 'WASTE_COMBUSTED',

  // ===== Freight =====
  FREIGHT_LAND: 'FREIGHT_LAND',
  FREIGHT_SEA: 'FREIGHT_SEA',
  FREIGHT_AIR: 'FREIGHT_AIR',

  // ===== Material Inputs =====
  MATERIAL_PURCHASED_GOODS: 'MATERIAL_PURCHASED_GOODS',
  MATERIAL_PURCHASED_SERVICES: 'MATERIAL_PURCHASED_SERVICES',
  MATERIAL_CAPITAL_GOODS: 'MATERIAL_CAPITAL_GOODS',
};

export const getActivityTypeOptions = (t) => {
  const groups = [
    {
      label: t('Fuel Combustion'),
      options: [
        { value: ActivityTypeEnum.FUEL_DIESEL, label: t('Diesel') },
        { value: ActivityTypeEnum.FUEL_LNG, label: t('LNG') },
        { value: ActivityTypeEnum.FUEL_BIODIESEL, label: t('Biodiesel') },
        { value: ActivityTypeEnum.FUEL_GASOLINE, label: t('Gasoline') },
        { value: ActivityTypeEnum.FUEL_NATURAL_GAS_GENERAL, label: t('Natural Gas (General)') },
        { value: ActivityTypeEnum.FUEL_NATURAL_GAS_HVAC, label: t('Natural Gas (HVAC)') },
        { value: ActivityTypeEnum.FUEL_PROPANE, label: t('Propane') },
        { value: ActivityTypeEnum.FUEL_COAL, label: t('Coal') },
        { value: ActivityTypeEnum.FUEL_OTHER_SOLID, label: t('Other Combustible Solids') },
        { value: ActivityTypeEnum.FUEL_OTHER_LIQUID, label: t('Other Combustible Liquids') },
        { value: ActivityTypeEnum.FUEL_OTHER_GAS, label: t('Other Combustible Gases') },
      ],
    },

    {
      label: t('Water Use'),
      options: [
        { value: ActivityTypeEnum.WATER_WITHDRAWN, label: t('Water Withdrawn') },
        { value: ActivityTypeEnum.WATER_DISCHARGED, label: t('Water Discharged') },
        { value: ActivityTypeEnum.WATER_RECYCLED, label: t('Water Recycled') },
      ],
    },

    {
      label: t('Electricity Use'),
      options: [
        { value: ActivityTypeEnum.ELECTRICITY_GENERAL, label: t('Electricity Use (General)') },
        { value: ActivityTypeEnum.ELECTRICITY_HVAC, label: t('Electricity Use (HVAC)') },
        { value: ActivityTypeEnum.ELECTRICITY_PRODUCTION, label: t('Electricity Use (Production/Manufacturing)') },
        { value: ActivityTypeEnum.ELECTRICITY_SOLAR_PV, label: t('Solar PV Generation') },
        { value: ActivityTypeEnum.ELECTRICITY_LIGHTING, label: t('Lighting') },
        { value: ActivityTypeEnum.ELECTRICITY_EV_CHARGING, label: t('Electric Car Charging') },
      ],
    },

    {
      label: t('HVAC'),
      options: [
        { value: ActivityTypeEnum.HVAC_OTHER, label: t('Other HVAC') },
        { value: ActivityTypeEnum.HVAC_REFRIGERANT, label: t('Refrigerant Use') },
        { value: ActivityTypeEnum.HVAC_DISTRICT_HEATING, label: t('District Heating') },
        { value: ActivityTypeEnum.HVAC_DISTRICT_COOLING, label: t('District Cooling') },
      ],
    },

    {
      label: t('Transportation'),
      options: [
        { value: ActivityTypeEnum.TRANSPORT_PETROL_CAR, label: t('Petrol Car/Van') },
        { value: ActivityTypeEnum.TRANSPORT_DIESEL_CAR, label: t('Diesel Car/Van') },
        { value: ActivityTypeEnum.TRANSPORT_ELECTRIC_CAR, label: t('Electric Car/Van') },
        { value: ActivityTypeEnum.TRANSPORT_HYBRID_CAR, label: t('Hybrid Car/Van') },
        { value: ActivityTypeEnum.TRANSPORT_PUBLIC, label: t('Public Transportation') },
        { value: ActivityTypeEnum.TRANSPORT_WALKING, label: t('Walking/Cycling') },
        { value: ActivityTypeEnum.TRANSPORT_FLIGHT, label: t('Flights') },
        { value: ActivityTypeEnum.TRANSPORT_MOTORCYCLE, label: t('Motorcycle') },
        { value: ActivityTypeEnum.TRANSPORT_BOAT, label: t('Boat/Ferry') },
      ],
    },

    {
      label: t('Waste'),
      options: [
        { value: ActivityTypeEnum.WASTE_HAZARDOUS, label: t('Hazardous Waste') },
        { value: ActivityTypeEnum.WASTE_NON_HAZARDOUS, label: t('Non-Hazardous Waste') },
        { value: ActivityTypeEnum.WASTE_RECYCLED, label: t('Recycled Waste') },
        { value: ActivityTypeEnum.WASTE_COMBUSTED, label: t('Combusted Waste') },
      ],
    },

    {
      label: t('Freight'),
      options: [
        { value: ActivityTypeEnum.FREIGHT_LAND, label: t('Freight by Land') },
        { value: ActivityTypeEnum.FREIGHT_SEA, label: t('Freight by Sea') },
        { value: ActivityTypeEnum.FREIGHT_AIR, label: t('Freight by Air') },
      ],
    },

    {
      label: t('Material Inputs'),
      options: [
        { value: ActivityTypeEnum.MATERIAL_PURCHASED_GOODS, label: t('Purchased Goods') },
        { value: ActivityTypeEnum.MATERIAL_PURCHASED_SERVICES, label: t('Purchased Services') },
        { value: ActivityTypeEnum.MATERIAL_CAPITAL_GOODS, label: t('Capital Goods') },
      ],
    },
  ];

  return groups;
};

export const formatCo2eFactor = (num, defaultValue = 0) => {
  if (['', undefined, null].includes(num) || isNaN(num)) {
    return defaultValue;
  }
  return formatDecimalNumber(num, 5)
    .replace(/(\.\d*?[1-9])0+$/, '$1')
    .replace(/\.0+$/, '');
};

export const ICON_BG = {
  danger: 'rgba(245, 73, 0, 0.1)',
  warning: 'rgba(255,193,7,0.1)',
  success: 'rgba(40,167,69,0.1)',
  primary: 'rgba(0,123,255,0.1)',
  secondary: 'rgba(108,117,125,0.1)',
  purple: 'rgba(127,34,254,0.1)',
};

export const getScenarioReportConfig = (t) => ({
  led: {
    header: {
      title: t('Scenario Outputs'),
      subtitle: t('Key performance indicators for LED upgrade'),
    },

    kpis: [
      {
        key: 'co2Reduction',
        title: t('Annual CO₂e Reduction'),
        unit: 'tCO₂e/year',
        variant: 'green',
        primary: true,
      },
      {
        key: 'annualSavings',
        title: t('Annual Savings'),
        unit: t('per year'),
        prefix: '£',
        variant: 'blue',
        primary: true,
      },
      {
        key: 'roi',
        title: t('Return on Investment'),
        unit: t('Annual ROI'),
        suffix: '%',
        variant: 'purple',
        primary: true,
      },
      {
        key: 'energySavings',
        title: t('Energy Savings'),
        unit: 'kWh/year',
      },
      {
        key: 'newLoad',
        title: t('New Load'),
        unit: 'kWh/year',
      },
      {
        key: 'payback',
        title: t('Payback Period'),
        unit: t('years'),
      },
    ],

    charts: {
      emissions: {
        title: t('CO₂e Emissions Comparison'),
        desc: t('Annual emissions: Current vs. With LED Upgrade'),
        labels: [
          t('Current'),
          t('With LED'),
        ],
      },
      cost: {
        title: t('Annual Cost Saving – Before & After'),
        desc: t(
          'Energy cost comparison: Current vs. With LED Upgrade'
        ),
        labels: [
          t('Before (Current Cost)'),
          t('After (With LED)'),
        ],
      },
    },
  },

  solar: {
    header: {
      title: t('Scenario Outputs'),
      subtitle: t('Key performance indicators for solar installation'),
    },

    kpis: [
      {
        key: 'co2Reduction',
        title: t('Annual CO₂e Reduction'),
        unit: 'tCO₂e/year',
        variant: 'green',
        primary: true,
      },
      {
        key: 'annualSavings',
        title: t('Annual Savings'),
        unit: t('per year'),
        prefix: '£',
        variant: 'blue',
        primary: true,
      },
      {
        key: 'roi',
        title: t('Return on Investment'),
        unit: t('Annual ROI'),
        suffix: '%',
        variant: 'purple',
        primary: true,
      },
    ],

    charts: {
      emissions: {
        title: t('CO₂e Emissions Comparison'),
        desc: t('Grid electricity vs. Solar generation'),
        labels: [
          t('Grid Electricity'),
          t('Solar Generation'),
        ],
      },
      cost: {
        title: t('Annual Cost Saving – Before & After'),
        desc: t('Electricity cost comparison'),
        labels: [
          t('Before (Grid Only)'),
          t('After (With Solar)'),
        ],
      },
    },
  },

  hvac: {
    header: {
      title: t('Scenario Outputs'),
      subtitle: t('Key performance indicators for HVAC optimization'),
    },

    kpis: [
      {
        key: 'co2Reduction',
        title: t('Annual CO₂e Reduction'),
        unit: 'tCO₂e/year',
        variant: 'green',
        primary: true,
      },
      {
        key: 'annualSavings',
        title: t('Annual Savings'),
        unit: t('per year'),
        prefix: '£',
        variant: 'blue',
        primary: true,
      },
      {
        key: 'roi',
        title: t('Return on Investment'),
        unit: t('Annual ROI'),
        suffix: '%',
        variant: 'purple',
        primary: true,
      },
    ],

    charts: {
      emissions: {
        title: t('CO₂e Emissions Comparison'),
        desc: t('Current vs. Optimized HVAC'),
        labels: [
          t('Current'),
          t('Optimized'),
        ],
      },
      cost: {
        title: t('Annual Cost Saving – Before & After'),
        desc: t('Energy cost comparison'),
        labels: [
          t('Before (Current Cost)'),
          t('After (Optimized)'),
        ],
      },
    },
  },

  temp: {
    header: {
      title: t('Scenario Outputs'),
      subtitle: t('Impact of reducing temperature by 1°C'),
    },

    kpis: [
      {
        key: 'co2Reduction',
        title: t('Annual CO₂e Reduction'),
        unit: 'tCO₂e/year',
        variant: 'green',
        primary: true,
      },
      {
        key: 'annualSavings',
        title: t('Annual Savings'),
        unit: t('per year'),
        prefix: '£',
        variant: 'purple',
        primary: true,
      },
      {
        key: 'electricitySavings',
        title: t('Electricity Savings'),
        unit: 'kWh/year',
      },
      {
        key: 'gasSavings',
        title: t('Gas Savings'),
        unit: 'kWh/year',
      },
      {
        key: 'totalEnergySavings',
        title: t('Total Energy Savings'),
        unit: 'kWh/year',
      },
    ],

    charts: {
      emissions: {
        title: t('CO₂e Emissions Comparison'),
        desc: t('Current vs. With 1°C Reduction'),
        labels: [
          t('Current'),
          t('Reduced'),
        ],
      },
      cost: {
        title: t('Annual Cost Saving – Before & After'),
        desc: t('Energy cost comparison'),
        labels: [
          t('Before (Current Cost)'),
          t('After (Reduced)'),
        ],
      },
    },
  },

  ppa: {
    header: {
      title: t('Scenario Outputs'),
      subtitle: t(
        'Environmental and financial impact of switching to green energy'
      ),
    },

    kpis: [
      {
        key: 'co2Reduction',
        title: t('Annual CO₂e Reduction'),
        unit: 'tCO₂e/year',
        variant: 'green',
        primary: true,
      },
      {
        key: 'additionalCost',
        title: t('Additional Annual Cost'),
        unit: t('per year'),
        prefix: '£',
        variant: 'orange',
        primary: true,
      },
      {
        key: 'roi',
        title: t('Return on Investment'),
        unit: t('Annual ROI'),
        suffix: '%',
        variant: 'purple',
        primary: true,
      },
    ],

    charts: {
      emissions: {
        title: t('CO₂e Emissions Comparison'),
        desc: t('Grid vs. Green Tariff'),
        labels: [
          t('Grid Electricity'),
          t('Green Electricity'),
        ],
      },
      cost: {
        title: t('Annual Cost Saving – Before & After'),
        desc: t('Electricity cost comparison'),
        labels: [
          t('Before (Grid)'),
          t('After (Green Tariff)'),
        ],
      },
    },
  },

  fleet: {
    header: {
      title: t('Scenario Outputs'),
      subtitle: t(
        'Key performance indicators for fleet electrification'
      ),
    },

    kpis: [
      {
        key: 'co2Reduction',
        title: t('Annual CO₂e Reduction'),
        unit: 'tCO₂e/year',
        variant: 'green',
        primary: true,
      },
      {
        key: 'annualSavings',
        title: t('Annual Savings'),
        unit: t('per year'),
        prefix: '£',
        variant: 'blue',
        primary: true,
      },
      {
        key: 'roi',
        title: t('Return on Investment'),
        unit: t('Annual ROI'),
        suffix: '%',
        variant: 'purple',
        primary: true,
      },
      {
        key: 'evEnergyRequired',
        title: t('EV Energy Required'),
        unit: 'kWh/year',
      },
      {
        key: 'fuelCost',
        title: t('Fuel Cost'),
        unit: t('per year'),
        prefix: '£',
      },
      {
        key: 'payback',
        title: t('Payback Period'),
        unit: t('years'),
      },
    ],

    charts: {
      emissions: {
        title: t('CO₂e Emissions Comparison'),
        desc: t('Fuel vs. Electric'),
        labels: [
          t('Fuel Vehicle'),
          t('Electric Vehicle'),
        ],
      },
      cost: {
        title: t('Annual Cost Saving – Before & After'),
        desc: t('Energy cost comparison'),
        labels: [
          t('Before (Fuel Cost)'),
          t('After (EV Charging)'),
        ],
      },
    },
  },
});


export const getScenarioDefinitions = (t) => ({
  led: {
    key: 'led',
    name: t('LED Lighting'),
    title: t('LED Lighting Upgrade'),
    subtitle: t('Switch to energy-efficient LED fixtures'),
    desc: t(
      'Model energy savings and ROI from switching to energy-efficient LED fixtures across your facility.'
    ),
    icon: 'bulb',
    color: 'warning',
  },

  solar: {
    key: 'solar',
    name: t('Solar PV'),
    title: t('Rooftop Solar Installation'),
    subtitle: t('Generate renewable energy onsite'),
    desc: t(
      'Model energy generation, carbon reduction, and financial returns from installing rooftop solar panels.'
    ),
    icon: 'sun',
    color: 'success',
  },

  hvac: {
    key: 'hvac',
    name: t('HVAC Optimization'),
    title: t('HVAC Optimization'),
    subtitle: t(
      'Improve heating, ventilation, and air conditioning efficiency'
    ),
    desc: t(
      'Calculate savings from optimizing HVAC systems through efficiency improvements and smart controls.'
    ),
    icon: 'wind',
    color: 'primary',
  },

  temp: {
    key: 'temp',
    name: t('Temperature Reduction'),
    title: t('Reduce Heating/Cooling by 1°C'),
    subtitle: t(
      'Lower thermostat settings to reduce energy consumption'
    ),
    desc: t(
      'Model the impact of reducing heating/cooling by 1°C on energy consumption and emissions.'
    ),
    icon: 'thermo',
    color: 'danger',
  },

  ppa: {
    key: 'ppa',
    name: t('GT / PPA'),
    title: t('Green Tariff / PPA'),
    subtitle: t(
      'Switch to renewable electricity contracts'
    ),
    desc: t(
      'Evaluate the costs and carbon benefits of switching to renewable energy tariffs or power purchase agreements.'
    ),
    icon: 'bolt',
    color: 'success',
  },

  fleet: {
    key: 'fleet',
    name: t('Fleet EV'),
    title: t('Fleet Electrification'),
    subtitle: t(
      'Switch from fuel vehicles to electric fleet'
    ),
    desc: t(
      'Compare costs and emissions between fuel-based and electric vehicles for your fleet operations.'
    ),
    icon: 'car',
    color: 'purple',
  },
});

export const getScenarioList = (t) =>
  Object.values(getScenarioDefinitions(t));

export const AI_INSIGHT_TYPE = {
  INFO: 'info',
  WARNING: 'warning',
  SUCCESS: 'success',
};

export const SCENARIO_AI_INSIGHTS = {
  // ================= LED =================
  led: [
    {
      type: 'success',
      title: 'Fast Payback Period',
      message:
        'With a payback of just 0.0 years, this LED upgrade delivers immediate financial returns. Energy savings will continue throughout the 15–20 year lifespan of LED fixtures.',
    },
    {
      type: 'success',
      title: 'Substantial Energy Savings',
      message:
        'Saving 85,000 kWh annually represents a significant reduction in electricity consumption, reducing approximately 20 tCO₂e per year.',
    },
    {
      type: 'info',
      title: 'Exceptional ROI',
      message:
        'The annual return significantly exceeds typical energy efficiency investments. Consider extending LED upgrades to additional facilities for compounded benefits.',
    },
    {
      type: 'warning',
      title: 'Operating Hours Sensitivity',
      message:
        'Savings are highly dependent on operating hours. Ensure fixtures operate for sufficient hours to maximize returns.',
    },
  ],

  // ================= SOLAR =================
  solar: [
    {
      type: 'success',
      title: 'Strong Carbon Reduction Impact',
      message:
        'Onsite solar generation significantly reduces grid electricity consumption, cutting annual emissions by approximately 120 tCO₂e.',
    },
    {
      type: 'info',
      title: 'Long-Term Cost Stability',
      message:
        'Solar installations hedge against future electricity price volatility by locking in predictable energy costs.',
    },
    {
      type: 'info',
      title: 'Moderate Payback Period',
      message:
        'A payback period of around 6 years is typical for rooftop solar projects and aligns well with long-term sustainability strategies.',
    },
    {
      type: 'warning',
      title: 'Roof Area Utilization',
      message:
        'Ensure sufficient roof space and structural capacity to support the proposed solar capacity before finalizing design.',
    },
  ],

  // ================= HVAC =================
  hvac: [
    {
      type: 'success',
      title: 'Efficient Energy Optimization',
      message:
        'Optimizing HVAC systems can deliver meaningful energy savings without major capital expenditure.',
    },
    {
      type: 'success',
      title: 'Balanced Financial Returns',
      message:
        'A payback period under 5 years makes HVAC optimization one of the most cost-effective efficiency measures.',
    },
    {
      type: 'info',
      title: 'Operational Comfort Maintained',
      message:
        'Energy reductions are achieved without compromising occupant comfort or indoor air quality.',
    },
    {
      type: 'warning',
      title: 'Performance Monitoring Required',
      message:
        'Regular monitoring and commissioning are recommended to ensure savings persist over time.',
    },
  ],

  // ================= TEMPERATURE REDUCTION =================
  temp: [
    {
      type: 'success',
      title: 'Low-Cost Efficiency Measure',
      message:
        'Reducing heating or cooling setpoints by just 1°C requires minimal investment while delivering immediate energy savings.',
    },
    {
      type: 'info',
      title: 'Behavior-Driven Savings',
      message:
        'The effectiveness of this measure depends on occupant behavior and adherence to temperature setpoints.',
    },
    {
      type: 'info',
      title: 'Very Short Payback',
      message:
        'Due to negligible implementation costs, payback is almost immediate.',
    },
    {
      type: 'warning',
      title: 'Comfort Sensitivity',
      message:
        'Care should be taken to ensure temperature changes do not negatively impact occupant comfort or productivity.',
    },
  ],

  // ================= GREEN TARIFF / PPA =================
  ppa: [
    {
      type: 'success',
      title: 'Significant Carbon Footprint Reduction',
      message:
        'Switching to a green tariff or PPA can dramatically reduce reported Scope 2 emissions.',
    },
    {
      type: 'info',
      title: 'No Operational Disruption',
      message:
        'This approach requires no onsite installation or operational changes, making it easy to implement.',
    },
    {
      type: 'warning',
      title: 'Potential Cost Increase',
      message:
        'Green tariffs may increase annual energy costs. Consider this option primarily for decarbonization goals rather than direct cost savings.',
    },
    {
      type: 'warning',
      title: 'Contractual Commitments',
      message:
        'Long-term PPAs may involve contractual obligations. Review terms carefully before committing.',
    },
  ],

  // ================= FLEET =================
  fleet: [
    {
      type: 'success',
      title: 'High Impact Decarbonization Opportunity',
      message:
        'Electrifying vehicle fleets can substantially reduce fuel-related emissions, delivering up to 85 tCO₂e reduction annually.',
    },
    {
      type: 'info',
      title: 'Operational Cost Savings',
      message:
        'Lower fuel and maintenance costs contribute to long-term financial savings compared to internal combustion vehicles.',
    },
    {
      type: 'info',
      title: 'Increased Electricity Demand',
      message:
        'Fleet electrification increases site electricity demand. Ensure grid capacity and charging infrastructure are adequately planned.',
    },
    {
      type: 'warning',
      title: 'Charging Infrastructure Planning',
      message:
        'Insufficient charging availability may impact fleet operations. Phased deployment is recommended.',
    },
  ],
};
